'use client'

import * as React from 'react'
import { cn } from '../../lib/utils'
import { Typography } from '../typography'

export interface TimerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Target date/time for countdown */
  targetDate: Date
  /** Callback when countdown reaches zero */
  onComplete?: () => void
  /** Show/hide labels */
  showLabels?: boolean
  /** Color variant */
  color?: 'base' | 'neutral' | 'positive' | 'negative'
  /** Language */
  language?: 'fa' | 'en'
}

interface TimeUnits {
  days: number
  hours: number
  minutes: number
  seconds: number
}

const persianLabels = {
  days: 'روز',
  hours: 'ساعت',
  minutes: 'دقیقه',
  seconds: 'ثانیه',
}
const englishLabels = {
  days: 'Days',
  hours: 'Hours',
  minutes: 'Minutes',
  seconds: 'Seconds',
}

const sizeStyles = {
  sm: {
    container: 'gap-2xs',
    timeUnit: 'gap-3xs',
    number: 'text-lg',
    label: 'text-xs',
  },
  md: {
    container: 'gap-xs',
    timeUnit: 'gap-2xs',
    number: 'text-xl',
    label: 'text-sm',
  },
  lg: {
    container: 'gap-sm',
    timeUnit: 'gap-xs',
    number: 'text-2xl',
    label: 'text-md',
  },
}

const colorStyles = {
  base: 'text-base-main',
  neutral: 'text-neutral-main',
  positive: 'text-positive-main',
  negative: 'text-negative-main',
}

function calculateTimeLeft(targetDate: Date): TimeUnits {
  const now = new Date().getTime()
  const target = targetDate.getTime()
  const difference = target - now

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 }
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24))
  const hours = Math.floor(
    (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  )
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((difference % (1000 * 60)) / 1000)

  return { days, hours, minutes, seconds }
}

export const Timer = React.forwardRef<HTMLDivElement, TimerProps>(
  (
    {
      targetDate,
      onComplete,
      showLabels = true,
      color = 'neutral',
      className,
      language = 'fa',
      ...props
    },
    ref,
  ) => {
    const [timeLeft, setTimeLeft] = React.useState<TimeUnits>(() =>
      calculateTimeLeft(targetDate),
    )

    React.useEffect(() => {
      const timer = setInterval(() => {
        const newTimeLeft = calculateTimeLeft(targetDate)
        setTimeLeft(newTimeLeft)

        if (
          newTimeLeft.days === 0 &&
          newTimeLeft.hours === 0 &&
          newTimeLeft.minutes === 0 &&
          newTimeLeft.seconds === 0
        ) {
          clearInterval(timer)
          onComplete?.()
        }
      }, 1000)

      return () => clearInterval(timer)
    }, [targetDate, onComplete])

    const sizeStyle = sizeStyles['sm']
    const colorStyle = colorStyles[color]

    const labels = language === 'fa' ? persianLabels : englishLabels

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-center rtl',
          sizeStyle.container,
          className,
        )}
        {...props}
      >
        <div className={cn('flex items-center gap-2xs', sizeStyle.timeUnit)}>
          <Typography
            variant='span'
            size={'sm'}
            weight='bold'
            className={colorStyle}
          >
            {timeLeft.days}
          </Typography>
          {showLabels && (
            <Typography
              variant='span'
              size={'2xs'}
              color='text-neutral-surface-80'
            >
              {labels.days}
            </Typography>
          )}
        </div>

        <div className={cn('flex items-center gap-2xs', sizeStyle.timeUnit)}>
          <Typography
            variant='span'
            size={'sm'}
            weight='bold'
            className={colorStyle}
          >
            {timeLeft.hours}
          </Typography>
          {showLabels && (
            <Typography
              variant='span'
              size={'2xs'}
              color='text-neutral-surface-80'
            >
              {labels.hours}
            </Typography>
          )}
        </div>

        <div className={cn('flex items-center gap-2xs', sizeStyle.timeUnit)}>
          <Typography
            variant='span'
            size={'sm'}
            weight='bold'
            className={colorStyle}
          >
            {timeLeft.minutes}
          </Typography>
          {showLabels && (
            <Typography
              variant='span'
              size={'2xs'}
              color='text-neutral-surface-80'
            >
              {labels.minutes}
            </Typography>
          )}
        </div>

        <div className={cn('flex items-center gap-2xs', sizeStyle.timeUnit)}>
          <Typography
            variant='span'
            size={'sm'}
            weight='bold'
            className={cn(colorStyle, 'w-[20px]')}
          >
            {timeLeft.seconds}
          </Typography>
          {showLabels && (
            <Typography
              variant='span'
              size={'2xs'}
              color='text-neutral-surface-80'
            >
              {labels.seconds}
            </Typography>
          )}
        </div>
      </div>
    )
  },
)

Timer.displayName = 'Timer'
